<script setup>
import { computed } from 'vue'
import Illustration from './Illustration.vue'
import { FEATURE_CARD_COLOURS, FEATURE_CARD_SAFE_EDGES, FEATURE_CARD_VARIATIONS, resolveFeatureCardColour, resolveFeatureCardVariation } from '@/utils/featureCardIllustrations'

const props = defineProps({
  variation:   { type: String, required: true },  // 'A'..'E', or 'random' — src/assets/illustrations/feature-card-<a-e>.svg
  colour:      { type: String, default: 'pink' },  // a key in FEATURE_CARD_COLOURS, or 'random'
  contentSide: { type: String, default: '' },      // 'left'|'right'|'top'|'bottom' — flips the artwork so a safe edge (see featureCardIllustrations.js) faces this side, when one exists
})

// Unseeded, so a 'random' colour re-rolls per render (including the
// client's post-hydration render) — see SmallCardTonalSplash.vue's note.
// ContentHero.vue passes a concrete, route-seeded key down instead.
const resolvedColour = computed(() => resolveFeatureCardColour(props.colour))

const set = computed(() => FEATURE_CARD_COLOURS[resolvedColour.value] || FEATURE_CARD_COLOURS.pink)

// Same 'random' resolution as colour, via the shared resolver (no seed
// here, so this still re-rolls per render if a caller ever passes
// variation="random" directly to this component - fine for a client-only
// consumer like ComponentGallery's demo, which has no prerendered HTML to
// disagree with. ContentHero.vue resolves its own seeded variation and
// passes a concrete letter down, so it never hits the unseeded branch.
const resolvedVariation = computed(() => resolveFeatureCardVariation(props.variation))

const illustrationName = computed(() => `feature-card-${resolvedVariation.value.toLowerCase()}`)

const OPPOSITE_EDGE = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' }

// Flip toward contentSide only when the *opposite* edge is the safe one —
// same logic as SmallCardTonalSplash.vue, re-pointed at FEATURE_CARD_SAFE_EDGES.
const flip = computed(() => {
  const safeEdges = FEATURE_CARD_SAFE_EDGES[resolvedVariation.value.toUpperCase()] || []
  if (!props.contentSide || safeEdges.includes(props.contentSide)) return { x: false, y: false }
  if (!safeEdges.includes(OPPOSITE_EDGE[props.contentSide])) return { x: false, y: false }
  const horizontal = props.contentSide === 'left' || props.contentSide === 'right'
  return { x: horizontal, y: !horizontal }
})

const illoStyle = computed(() => ({
  '--illo-1': set.value.colour1,
  '--illo-2': set.value.colour2,
  transform: `scale(${flip.value.x ? -1 : 1}, ${flip.value.y ? -1 : 1})`,
}))
</script>

<template>
  <div class="feature-tonal-splash" :style="{ background: set.background }">
    <Illustration :name="illustrationName" :style="illoStyle" />
  </div>
</template>

<style lang="scss" scoped>
.feature-tonal-splash {
  display: block;
  width: 100%;
  // Native feature-card viewBox ratio (546x464) — see SmallCardTonalSplash.vue
  // for the override/crop-to-fill rationale.
  aspect-ratio: 546 / 464;
  @include rounded($radius);
  overflow: hidden;

  // Illustration.vue's own root span is display:inline-block with no
  // explicit size - without this override, an ancestor's size override
  // (e.g. ContentHero.vue's absolutely-positioned corner tile) never
  // reaches the actual <svg>, which then falls back to its own default
  // intrinsic size instead of filling this box. Same fix applied to
  // SmallCardTonalSplash.vue and VerticalSplash.vue - all three share this
  // exact Illustration.vue wrapping pattern.
  :deep(.illustration) {
    display: block;
    width: 100%;
    height: 100%;
  }

  // Must be scoped *under* .illustration to out-specify Illustration.vue's own
  // `.illustration[data-v-…] svg { height: auto }`. A bare `:deep(svg)` ties it
  // on specificity and then wins or loses purely on stylesheet order — and it
  // was losing, so the svg kept sizing off its own intrinsic ratio (measured
  // 425x361 inside a 425x420 tile). With the box driving height, the SVG's own
  // preserveAspectRatio="xMidYMid slice" crops to fill instead of letterboxing.
  :deep(.illustration svg) {
    height: 100%;
  }
}
</style>
